import {
    FundParseOutputFile,
    FundParseOutputManifest,
    FundParseOutputManifestGroup
} from "./fundParseTypes";
import { OUTPUT_MANIFEST_FILENAME, POSITION_MAPPINGS_FILENAME } from "./fundParseUtils";
import { EXPOSURE_CHARTS_FILENAME } from "./fundParseExposureChartUtils";

const GROUP_CLIENT = "client_deliverables";
const GROUP_XLSX = "xlsx";
const GROUP_CSV = "csv";
const GROUP_TXT = "txt";

const GROUP_DEFINITIONS: Record<string, { id: string; label: string; defaultExpanded: boolean }> = {
    [GROUP_CLIENT]: {
        id: GROUP_CLIENT,
        label: "Client deliverables (CSV)",
        defaultExpanded: true
    },
    [GROUP_XLSX]: {
        id: GROUP_XLSX,
        label: "XLSX group",
        defaultExpanded: false
    },
    [GROUP_CSV]: {
        id: GROUP_CSV,
        label: "CSV group",
        defaultExpanded: true
    },
    [GROUP_TXT]: {
        id: GROUP_TXT,
        label: "TXT group",
        defaultExpanded: true
    }
};

function classifyOutputFile(fileName: string): [string | null, string, boolean] {
    const baseName = fileName.split("/").pop() || fileName;

    if (baseName === POSITION_MAPPINGS_FILENAME) {
        return [null, "Position mapping (internal)", false];
    }
    if (baseName === EXPOSURE_CHARTS_FILENAME) {
        return [null, "Exposure charts (internal)", false];
    }
    if (baseName.endsWith("_canonical.xlsx")) {
        return [null, "", false];
    }

    const lookthroughCsv = baseName.match(/^(\d{8})_(Bond|Equity|Cash)LookThrough\.csv$/);
    if (lookthroughCsv) {
        return [GROUP_CLIENT, `${lookthroughCsv[2]} look-through CSV`, true];
    }

    const lookthroughXlsx = baseName.match(/^(\d{8})_(Bond|Equity|Cash)LookThrough\.xlsx$/);
    if (lookthroughXlsx) {
        return [GROUP_XLSX, `${lookthroughXlsx[2]} look-through XLSX`, true];
    }

    if (/^\d{8}_ConsolidatedTPT\.xlsx$/.test(baseName)) {
        return [GROUP_XLSX, "Consolidated TPT workbook", true];
    }
    if (/^\d{8}_ESG_input\.xlsx$/.test(baseName)) {
        return [GROUP_XLSX, "ESG input workbook", true];
    }
    if (/^\d{8}_ExcludedPositions\.xlsx$/.test(baseName)) {
        return [GROUP_XLSX, "Excluded positions (derivatives / unclassified)", true];
    }
    if (/^\d{8}_ProcessingLog\.txt$/.test(baseName)) {
        return [GROUP_TXT, "Processing log", true];
    }

    if (baseName.endsWith(".xlsx")) {
        return [GROUP_XLSX, baseName, true];
    }
    if (baseName.endsWith(".txt")) {
        return [GROUP_TXT, baseName, true];
    }

    return [GROUP_CSV, baseName, true];
}

export function inferPeriodFromFiles(files: FundParseOutputFile[]): string | null {
    for (const file of files) {
        const baseName = file.fileName.split("/").pop() || file.fileName;
        const match = baseName.match(/^(\d{8})_/);
        if (match) {
            return match[1];
        }
    }
    return null;
}

export async function buildOutputManifest(
    outputFiles: FundParseOutputFile[],
    valuationDate: string
): Promise<{ manifest: FundParseOutputManifest; files: FundParseOutputFile[] }> {
    const entries = outputFiles.map((file) => {
        const [groupId, label, downloadable] = classifyOutputFile(file.fileName);
        return {
            fileName: file.fileName,
            label,
            downloadable,
            groupId
        };
    });

    const nextFiles = [...outputFiles];

    const groupedFiles: Record<string, FundParseOutputManifestGroup["files"]> = {
        [GROUP_CLIENT]: [],
        [GROUP_XLSX]: [],
        [GROUP_CSV]: [],
        [GROUP_TXT]: []
    };
    const internalFiles: FundParseOutputManifestGroup["files"] = [];

    for (const entry of entries) {
        const manifestEntry = {
            fileName: entry.fileName,
            label: entry.label,
            downloadable: entry.downloadable
        };
        if (!entry.groupId || !entry.downloadable) {
            internalFiles.push(manifestEntry);
            continue;
        }
        if (entry.groupId in groupedFiles) {
            groupedFiles[entry.groupId].push(manifestEntry);
        }
    }

    const groups: FundParseOutputManifestGroup[] = [];
    for (const groupId of [GROUP_CLIENT, GROUP_XLSX, GROUP_CSV, GROUP_TXT]) {
        const files = groupedFiles[groupId];
        if (!files.length) {
            continue;
        }
        const definition = GROUP_DEFINITIONS[groupId];
        groups.push({
            id: definition.id,
            label: definition.label,
            defaultExpanded: definition.defaultExpanded,
            files
        });
    }

    const manifest: FundParseOutputManifest = {
        valuationDate,
        groups,
        internalFiles
    };

    const manifestJson = JSON.stringify(manifest, null, 2);
    const manifestFile: FundParseOutputFile = {
        fileName: OUTPUT_MANIFEST_FILENAME,
        base64: btoa(unescape(encodeURIComponent(manifestJson))),
        mimeType: "application/json"
    };

    return {
        manifest,
        files: [...nextFiles, manifestFile]
    };
}

export function textToBase64(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let binary = "";
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

export function jsonToOutputFile(fileName: string, payload: unknown): FundParseOutputFile {
    const jsonText = JSON.stringify(payload, null, 2);
    return {
        fileName,
        base64: textToBase64(jsonText),
        mimeType: "application/json"
    };
}
